import { useCallback, useEffect, useState } from 'react';

import { logger } from '../lib/logger';

import { useDebouncedUpdate } from './useDebouncedUpdate';

const inputLogger = logger.child('[useNarrativeTextInput]');

/**
 * Hook for managing text input in narrative editor with local state and debounced updates
 * Provides immediate UI feedback while debouncing store updates
 */
export function useNarrativeTextInput(
  initialValue: string,
  onUpdate: (value: string) => void,
  delay: number = 300,
  maxLength?: number
) {
  // Local state for immediate UI updates
  const [localValue, setLocalValue] = useState(initialValue);
  const [error, setError] = useState<string | undefined>(undefined);

  inputLogger.debug('Hook initialized', { initialValue, delay });

  // Debounced update function
  const debouncedUpdate = useDebouncedUpdate(onUpdate, delay);

  // Update local state when initial value changes (e.g., when switching nodes or external updates)
  useEffect(() => {
    // Only update localValue if initialValue is TRULY different from the current localValue.
    // This prevents resetting the input while the user is typing if the initialValue
    // (from the store) hasn't caught up yet due to debouncing, or if initialValue
    // transiently flickers during a parent re-render.
    if (initialValue !== localValue) {
      inputLogger.debug('Initial value changed externally, updating localValue', {
        from: localValue,
        to: initialValue,
      });
      setLocalValue(initialValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]); // Crucially, localValue is removed from the dependency array.

  // Handle change - update local state immediately, debounce store update
  const handleChange = useCallback(
    (value: string) => {
      console.log('useNarrativeTextInput handleChange:', { from: localValue, to: value });
      inputLogger.debug('Handle change', { from: localValue, to: value });
      let processedValue = value;
      if (maxLength && processedValue.length > maxLength) {
        processedValue = processedValue.slice(0, maxLength);
        setError(`Maximum length of ${maxLength} characters reached`);
      } else {
        setError(undefined);
      }
      setLocalValue(processedValue);
      debouncedUpdate(processedValue);
    },
    [debouncedUpdate, localValue, maxLength]
  );

  return {
    value: localValue,
    onChange: handleChange,
    error,
  };
}

/**
 * Hook for managing choice text input with specific handling for choices array
 */
export function useNarrativeChoiceInput(
  choices: Array<{ text: string; [key: string]: any }> | undefined,
  onUpdateChoice: (index: number, field: 'text' | 'npcResponse', value: string) => void,
  delay: number = 300,
  maxLength?: number
) {
  // Create debounced version of the update function
  const debouncedUpdateChoice = useDebouncedUpdate(onUpdateChoice, delay);

  // Local state for all choices
  const [localChoices, setLocalChoices] = useState(choices || []);

  // Update local state when choices prop changes
  useEffect(() => {
    setLocalChoices(choices || []);
  }, [choices]);

  // Handle choice change
  const handleChoiceChange = useCallback(
    (index: number, field: 'text' | 'npcResponse', value: string) => {
      // Update local state immediately
      let processedValue = value;
      if (maxLength && processedValue.length > maxLength) {
        processedValue = processedValue.slice(0, maxLength);
      }
      setLocalChoices((prev) => {
        const newChoices = [...prev];
        if (newChoices[index]) {
          newChoices[index] = { ...newChoices[index], [field]: processedValue };
        }
        return newChoices;
      });

      // Debounce the store update
      debouncedUpdateChoice(index, field, processedValue);
    },
    [debouncedUpdateChoice, maxLength]
  );

  return {
    choices: localChoices,
    onChoiceChange: handleChoiceChange,
  };
}
